import {
  Button,
  Col,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Typography,
} from 'antd';
import { addWeeks, subWeeks } from 'date-fns';
import { useEffect, useState } from 'react';
import {
  daysInWeeks,
  rosterDisplayModes,
  daysInWeek,
  rosterDateTitle,
} from './lib/util';
import type { RosterDisplayMode } from './types';

const { Title } = Typography;

type RosterDatePickerProps = {
  /**
   * Callback whenever the selected dates are updated.
   */
  readonly datesUpdated: (dates: readonly Date[]) => void;
  /**
   * Callback whenever the display mode is updated.
   */
  readonly displayModeUpdated: (displayMode: RosterDisplayMode) => void;
};

/**
 * Buttons for selecting the displayed roster date range
 * @param props the props for the date picker.
 * @returns a bar of buttons for changing the date on the roster
 */
const RosterDatePicker = ({
  datesUpdated,
  displayModeUpdated,
}: RosterDatePickerProps) => {
  const todayDate = new Date();

  // The date that sets how we retrieve the week days or month days.
  // This date must be within the week/4 weeks days we display.
  // Init to current day, but it can change when we page through the roster table (to different week or 4 weeks).
  const [anchorDate, setAnchorDate] = useState(todayDate);

  const [rosterDisplayMode, setRosterDisplayMode] =
    useState<RosterDisplayMode>('weekly');

  useEffect(() => {
    const rosterDates: readonly Date[] =
      rosterDisplayMode === 'weekly'
        ? daysInWeek(anchorDate)
        : daysInWeeks(anchorDate, 4);

    datesUpdated(rosterDates);

    displayModeUpdated(rosterDisplayMode);
  }, [anchorDate, rosterDisplayMode, datesUpdated, displayModeUpdated]);

  const nextAnchorDate = () =>
    rosterDisplayMode === 'weekly'
      ? setAnchorDate(addWeeks(anchorDate, 1))
      : setAnchorDate(addWeeks(anchorDate, 4));

  const prevAnchorDate = () =>
    rosterDisplayMode === 'weekly'
      ? setAnchorDate(subWeeks(anchorDate, 1))
      : setAnchorDate(subWeeks(anchorDate, 4));

  const todayAnchorDate = () => setAnchorDate(todayDate);

  const onDisplayModeChange = ({ target }: RadioChangeEvent) => {
    // Antd radio target value is any type.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setRosterDisplayMode(target.value);
  };

  return (
    <Row wrap={false}>
      <Col span={8}></Col>
      <Col
        span={8}
        style={{ marginBottom: '20px', textAlign: 'center', width: '100%' }}
      >
        <Title level={3}>
          {rosterDateTitle(rosterDisplayMode, anchorDate)}
        </Title>
      </Col>

      <Col span={8}>
        <Space style={{ float: 'right' }}>
          <Button type="default" onClick={todayAnchorDate}>
            Today
          </Button>
          <Button type="default" onClick={prevAnchorDate}>
            &#8249; Prev
          </Button>
          <Button type="default" onClick={nextAnchorDate}>
            Next &#8250;
          </Button>
          <Radio.Group
            options={[...rosterDisplayModes]}
            onChange={onDisplayModeChange}
            value={rosterDisplayMode}
            optionType="button"
            buttonStyle="solid"
            size={'small'}
          />
        </Space>
      </Col>
    </Row>
  );
};

export default RosterDatePicker;
